import React from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpToLine,
  Calendar,
  Check,
  Edit2,
  Star,
  Trash2,
} from 'lucide-react';
import { MobileFullScreenModal, ModalDoneFooter } from '../../../ui/MobileFullScreenModal';
import type { TaskItem } from '../../../../types/topic';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { logger } from '../../../../utils/logger';
import { getStatusColor } from '../../../../utils/colorUtils';

interface MobileTaskMenuProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskItem;
  onEdit: () => void;
  onDelete: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onMoveTask?: (id: string, direction: 'up' | 'down' | 'top') => void;
  onOpenDatePicker: () => void;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * Mobile-optimized full-screen menu for task actions
 * Replaces the dropdown menu in task cards on mobile devices
 */
export function MobileTaskMenu({
  isOpen,
  onClose,
  task,
  onEdit,
  onDelete,
  onUpdateTask,
  onMoveTask,
  onOpenDatePicker,
  isFirst,
  isLast,
}: MobileTaskMenuProps) {
  // Get columns from React Query for status options
  // Handle both snake_case and camelCase versions based on data source
  const topicId = task.topicId !== undefined ? task.topicId : task.topic_id;
  const { data: columns = [] } = useTaskColumns(topicId);

  // Only render when open
  if (!isOpen) return null;

  // Add a render log to confirm this is the component being opened
  logger.component('MobileTaskMenu rendered', { 
    taskId: task.id,
    isFirst,
    isLast
  });

  // Get all columns except the current one, maintaining the original order
  const otherColumns = columns.filter(col => col.status !== task.status);

  // Sort otherColumns to maintain the original column order
  otherColumns.sort((a, b) => {
    return (
      columns.findIndex(col => col.status === a.status) -
      columns.findIndex(col => col.status === b.status)
    );
  });

  // Handler for status change
  const handleMoveStatus = (newStatus: TaskItem['status']) => {
    logger.component('Moving task status', { taskId: task.id, newStatus });
    onUpdateTask(task.id, { status: newStatus });
    onClose();
  };

  // Handler for highlight toggle
  const handleToggleHighlight = () => {
    // Handle both snake_case and camelCase versions based on data source
    const isHighlighted = task.isHighlighted !== undefined ? task.isHighlighted : task.is_highlighted;
    
    logger.component('Toggling highlight state', {
      taskId: task.id,
      currentHighlightState: isHighlighted,
      newHighlightState: !isHighlighted,
    });
    
    // Use the field name expected by the backend (snake_case)
    onUpdateTask(task.id, { is_highlighted: !isHighlighted });
    onClose();
  };

  // Handler for delete
  const handleDelete = () => {
    logger.component('Delete button clicked', { taskId: task.id });
    onDelete(task.id);
    onClose();
  };

  // Handler for edit
  const handleEdit = e => {
    e.stopPropagation();
    onEdit();
    onClose();
  };

  return (
    <MobileFullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      title="Task Options"
      data-task-menu="true"
      footerContent={<ModalDoneFooter onClose={onClose} />}
    >
      {/* Main Actions */}
      <div>
        <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
          <h3 className="font-medium text-gray-700">Actions</h3>
        </div>

        {/* Edit */}
        <button
          onClick={e => handleEdit(e)}
          className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
        >
          <div className="flex-1 flex items-center gap-2">
            <Edit2 size={16} className="text-blue-500" />
            <span>Edit Task</span>
          </div>
        </button>

        {/* Change Deadline */}
        <button
          onClick={() => {
            // Open the date picker first
            onOpenDatePicker();
            // Then close the menu
            onClose();
          }}
          className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
        >
          <div className="flex-1 flex items-center gap-2">
            <Calendar size={16} className="text-blue-500" />
            <span>{task.deadline ? 'Edit Deadline' : 'Add Deadline'}</span>
          </div>
        </button>

        {/* Highlight Task */}
        <button
          onClick={handleToggleHighlight}
          className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
        >
          <div className="flex-1 flex items-center gap-2">
            <Star size={16} className={(task.isHighlighted !== undefined ? task.isHighlighted : task.is_highlighted) ? 'text-yellow-500' : 'text-blue-500'} />
            <span>{(task.isHighlighted !== undefined ? task.isHighlighted : task.is_highlighted) ? 'Remove Highlight' : 'Highlight Task'}</span>
          </div>
        </button>

        {/* Delete Task */}
        <button
          onClick={handleDelete}
          className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
        >
          <div className="flex-1 flex items-center gap-2">
            <Trash2 size={16} className="text-red-500" />
            <span className="text-red-500">Delete Task</span>
          </div>
        </button>
      </div>

      {/* Reordering Options */}
      {onMoveTask && (!isFirst || !isLast) && (
        <div className="mt-2">
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Reorder</h3>
          </div>

          {!isFirst && (
            <>
              {/* Only show Move to Top if the task isn't already near the top */}
              <button
                onClick={() => {
                  onMoveTask(task.id, 'top');
                  onClose();
                }}
                className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex-1 flex items-center gap-2">
                  <ArrowUpToLine size={16} className="text-blue-500" />
                  <span>Move to Top</span>
                </div>
              </button>
              
              <button
                onClick={() => {
                  onMoveTask(task.id, 'up');
                  onClose();
                }}
                className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex-1 flex items-center gap-2">
                  <ArrowUp size={16} className="text-blue-500" />
                  <span>Move Up</span>
                </div>
              </button>
            </>
          )}

          {!isLast && (
            <button
              onClick={() => {
                onMoveTask(task.id, 'down');
                onClose();
              }}
              className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex-1 flex items-center gap-2">
                <ArrowDown size={16} className="text-blue-500" />
                <span>Move Down</span>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Status Change Options */}
      {otherColumns.length > 0 && (
        <div className="mt-2">
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Move to Column</h3>
          </div>

          {otherColumns.map(column => (
            <button
              key={column.status}
              onClick={() => handleMoveStatus(column.status)}
              className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex-1 flex items-center gap-2">
                {column.status === 'done' ? (
                  <Check size={16} className="text-green-500" />
                ) : columns.findIndex(c => c.status === column.status) <
                  columns.findIndex(c => c.status === task.status) ? (
                  <ArrowLeft size={16} className={getStatusColor(column.status, columns, 'icon')} />
                ) : (
                  <ArrowRight size={16} className={getStatusColor(column.status, columns, 'icon')} />
                )}
                <span>{column.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </MobileFullScreenModal>
  );
}

export default MobileTaskMenu;
